import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that keeps the quantity discount tier price shown in sync with the live
 * quantity selector.
 */
class PriceOfferQuantityComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }

    this.#syncFromInput();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getProductForm() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    const { productId } = this.dataset;

    if (productId) {
      return scope.querySelector(`product-form-component[data-product-id="${productId}"]`);
    }

    return scope.querySelector('product-form-component');
  }

  #getQuantityValue() {
    const productForm = this.#getProductForm();
    const quantitySelector = productForm?.querySelector('quantity-selector-component');
    if (!quantitySelector || typeof quantitySelector.getValue !== 'function') return null;

    const value = Number.parseInt(quantitySelector.getValue(), 10);
    return Number.isNaN(value) ? null : value;
  }

  /**
   * Shows the pre-rendered price for the highest configured tier quantity that does not exceed
   * the given quantity (so a single tier covers every quantity up to the next one), and hides
   * every other tier. Falls back to the no-discount "default" tier below the lowest one.
   * @param {number} quantity
   */
  #setActiveTier(quantity) {
    const tierGroups = this.querySelectorAll('[data-price-tiers]');

    for (const group of tierGroups) {
      if (!(group instanceof HTMLElement)) continue;

      const tiers = group.querySelectorAll('[data-price-qty-min]');

      /** @type {HTMLElement | null} */
      let matched = null;
      let matchedQty = -1;

      for (const tier of tiers) {
        if (!(tier instanceof HTMLElement) || tier.dataset.priceQtyMin === 'default') continue;
        const tierQty = Number.parseInt(tier.dataset.priceQtyMin ?? '', 10);
        if (Number.isNaN(tierQty) || tierQty > quantity) continue;
        if (tierQty > matchedQty) {
          matchedQty = tierQty;
          matched = tier;
        }
      }

      if (!matched) {
        matched = group.querySelector('[data-price-qty-min="default"]');
      }

      for (const tier of tiers) {
        if (!(tier instanceof HTMLElement)) continue;
        tier.hidden = tier !== matched;
      }
    }
  }

  #syncFromInput() {
    const quantity = this.#getQuantityValue();
    if (quantity === null) return;
    this.#setActiveTier(quantity);
  }

  #onQuantityUpdate = (event) => {
    if (!(event.detail?.quantity >= 0)) return;

    const productForm = this.#getProductForm();
    if (productForm && event.target instanceof Node && !productForm.contains(event.target)) return;

    this.#setActiveTier(event.detail.quantity);
  };

  #onVariantUpdate = (event) => {
    if (event.detail?.data?.newProduct?.id) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newHtml = event.detail?.data?.html;
    if (!newHtml || !this.dataset.blockId) return;

    const newComponent = newHtml.querySelector(
      `price-offer-quantity-component[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newComponent) return;

    this.innerHTML = newComponent.innerHTML;
    if (newComponent.dataset.productId) {
      this.dataset.productId = newComponent.dataset.productId;
    }

    this.#syncFromInput();
  };
}

if (!customElements.get('price-offer-quantity-component')) {
  customElements.define('price-offer-quantity-component', PriceOfferQuantityComponent);
}
